import { Grid, Heading, Stack, Image, Box, Text } from "@chakra-ui/react";
import { useState } from "react";
import { motion } from "framer-motion";

import IconLeftArrow from "components/Chakra/Icons/LeftArrow";
import IconRightArrow from "components/Chakra/Icons/RightArrow";
import Label from "components/Label";
import CustomLink from "components/Chakra/CustomLink";
import CardMotion from "components/Chakra/Card/CardMotion";
import PhotoCard from "./TabsWrapper/PhotoCard";
import { IPhoto } from "utils/offer";

type Card = {
  id: number;
  title: string;
  informations: Array<{
    name: string;
    labels?: Array<string>;
    text?: string;
  }>;
  image: string;
  matterportLink: string;
  galleryItems: Array<IPhoto>;
};

const data: Array<Card> = [
  {
    id: 1,
    title: "Prodej luxusního rodinného domu v Praze 13 – Stodůlky",
    informations: [
      {
        name: "Výstupy",
        labels: ["fotografie", "3D virtuální scan"],
      },
      {
        name: "Podlahová plocha",
        text: "215 m2",
      },
      {
        name: "Dispozice",
        text: "dům",
      },
    ],
    matterportLink: "https://my.matterport.com/show/?m=9VQkEgDkqAi",
    image: "/img/scenes/Matterport/prahaMT.png",
    galleryItems: [
      {
        path: "/img/scenes/Matterport/1-1.png",
        id: 11,
      },
      {
        path: "/img/scenes/Matterport/1-2.png",
        id: 12,
      },
      {
        path: "/img/scenes/Matterport/1-3.png",
        id: 13,
      },
    ],
  },
  {
    id: 2,
    title: "Prodej domu Lipová 840, Chýně",
    informations: [
      {
        name: "Výstupy",
        labels: ["fotografie", "3D scan", "půdorys"],
      },
      {
        name: "Podlahová plocha",
        text: "202 m2",
      },
      {
        name: "Dispozice",
        text: "dům",
      },
    ],
    matterportLink: "https://my.matterport.com/show/?m=YRziMZwu5ry",
    image: "/img/scenes/Matterport/matterport-example.png",
    galleryItems: [
      {
        path: "/img/scenes/Matterport/2-1.png",
        id: 21,
      },
      {
        path: "/img/scenes/Matterport/2-2.png",
        id: 22,
      },
      {
        path: "/img/scenes/Matterport/2-3.png",
        id: 23,
      },
    ],
  },
  {
    id: 3,
    title: "Prodej domu Olomouc Ostružinová",

    informations: [
      {
        name: "Výstupy",
        labels: ["fotografie", "3D scan", "půdorys"],
      },
      {
        name: "Podlahová plocha",
        text: "177 m2",
      },
      {
        name: "Dispozice",
        text: "dům",
      },
    ],
    matterportLink: "https://my.matterport.com/show/?m=vjE2qfqzu9S",
    image: "/img/scenes/Matterport/olomoucMT.png",
    galleryItems: [
      {
        path: "/img/scenes/Matterport/3-1.png",
        id: 31,
      },
      {
        path: "/img/scenes/Matterport/3-2.png",
        id: 32,
      },
      {
        path: "/img/scenes/Matterport/3-3.png",
        id: 33,
      },
    ],
  },
];

const fadeVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const ExampleRealizations = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : data.length - 1,
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex < data.length - 1 ? prevIndex + 1 : 0,
    );
  };

  return (
    <Stack
      justify="center"
      align="center"
      backgroundColor="ud-grey-100"
      py={{ base: "64px", md: "80px" }}
      px="16px"
    >
      <Grid
        maxW="1360px"
        w="100%"
        rowGap={{ base: "24px", md: "40px" }}
        columnGap={{ base: "20px" }}
        gridTemplateAreas={`
    "TITLE TITLE TITLE ARROWS"
    "CONTENT CONTENT CONTENT CONTENT"
    `}
      >
        <Heading gridArea="TITLE" pb={{ base: "20px" }}>
          Ukázky realizací
        </Heading>
        <Stack gridArea="ARROWS" direction="row" align="center" justify="end">
          <Stack
            border="1px solid black"
            borderRadius="100%"
            w="fit-content"
            h="fit-content"
            align="center"
            p={1}
            justify="center"
            cursor="pointer"
            onClick={handlePrev}
            _hover={{ backgroundColor: "ud-grey-200" }}
            transition="background-color 0.3s"
          >
            <IconLeftArrow />
          </Stack>
          <Stack
            border="1px solid black"
            borderRadius="100%"
            w="fit-content"
            h="fit-content"
            align="center"
            p={1}
            justify="center"
            cursor="pointer"
            onClick={handleNext}
            _hover={{ backgroundColor: "ud-grey-200" }}
            transition="background-color 0.3s"
          >
            <IconRightArrow />
          </Stack>
        </Stack>

        <motion.div
          style={{
            gridArea: "CONTENT",
          }}
          key={currentIndex}
          variants={fadeVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 1 }}
        >
          <Stack direction={{ base: "column", md: "row" }} gap="20px">
            <Stack
              p="32px"
              borderRadius="16px"
              backgroundColor="ud-white"
              width={{ base: "100%", md: "325px" }}
              justify="space-between"
            >
              <Heading>{data[currentIndex].title}</Heading>
              <Box as="ul">
                {data[currentIndex].informations.map((info, index) => (
                  <Stack
                    key={index}
                    direction="row"
                    borderBottom="1px solid"
                    borderColor="ud-grey-200"
                    _last={{
                      border: "none",
                    }}
                    py="16px"
                    align="center"
                  >
                    <Text flex={1}>{info.name}</Text>
                    <Stack
                      direction={{ base: "row", md: "column" }}
                      wrap="wrap"
                      justify={{ base: "end" }}
                      align={{ md: "flex-end" }}
                      spacing={0}
                      gap={2}
                    >
                      {info.labels?.map((label, i) => (
                        <Label
                          key={i}
                          message={label}
                          backgroundColor="inherit"
                          borderColor="ud-gray-350"
                          borderWidth="1px"
                          borderStyle="solid"
                          textTransform="capitalize"
                          borderRadius="full"
                          p="2px 8px"
                          w="fit-content"
                        />
                      ))}
                    </Stack>
                    {info.text?.length && (
                      <Text fontWeight="bold">{info.text}</Text>
                    )}
                  </Stack>
                ))}
              </Box>
            </Stack>
            <Stack flex={{ sm: 1 }}>
              <CustomLink
                href={data[currentIndex].matterportLink}
                display={{ base: "contents", md: "flex" }}
                target="_blank"
                _hover={{
                  filter: "brightness(60%)",
                }}
              >
                <Image
                  src={data[currentIndex].image}
                  alt={data[currentIndex].title}
                  objectFit="cover"
                  h={{ base: "185px", md: "450px" }}
                  w="100%"
                  borderRadius="16px"
                />
              </CustomLink>
            </Stack>
            <Stack width={{ base: "100%", md: "195px" }} spacing={0} gap="20px">
              {data[currentIndex].galleryItems.map((item, index) => (
                <CardMotion
                  key={index}
                  isZoomIn
                  isCenterIcon
                  isGalerryIcon
                  content={
                    <PhotoCard
                      itemsGallery={data[currentIndex].galleryItems}
                      id={item.id}
                    />
                  }
                  gridItemSetting={{
                    h: { base: "185px", md: "136px" },
                  }}
                  filterBrightnessPercent={60}
                />
              ))}
            </Stack>
          </Stack>
        </motion.div>
      </Grid>
    </Stack>
  );
};
export default ExampleRealizations;
